import { RadioButton } from 'cpool-components/input/RadioButton';
import type { ApplicantCreation } from 'cpool-schemas';
import type { Path } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SubTitle } from './shared/SubTitle';
import { TextInput } from 'cpool-components/input/TextInput';

import { FormDateInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDateInput';
import { FormDropdown } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDropdown';
import { FormCurrencyInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormCurrencyInput';
import { useBranchOptions, useCountryOptions } from '~/features/leadDetails/components/Overview/CustomerData/shared/useDropdownOptions';

const basePath = 'beschaeftigung.beamter.beschaeftigungsverhaeltnis' as const;

export const Official: React.FC = () => {
    const { t } = useTranslation();
    const { control, register, watch } = useFormContext<ApplicantCreation>();
    const branchOptions = useBranchOptions();
    const countryOptions = useCountryOptions();

    const inProbezeit = watch(`${basePath}.inProbezeit` as Path<ApplicantCreation>);

    return (
        <div className="mt-4 space-y-2">
            <FormDropdown
                id={`${basePath}.arbeitgeber.branche` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.BRANCH')}
                options={branchOptions}
                control={control}
            />

            <TextInput
                id={`${basePath}.berufsbezeichnung`}
                label={t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.EMPLOYMENT_TYPE')}
                labelClassName="w-[175px] justify-start"
                {...register(`${basePath}.berufsbezeichnung` as Path<ApplicantCreation>)}
            />

            <FormDateInput
                id={`${basePath}.beschaeftigtSeit` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.EMPLOYED_SINCE')}
                control={control}
            />

            <FormDateInput
                id={`${basePath}.verbeamtetSeit` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.CIVIL_SERVANT_SINCE')}
                control={control}
            />

            <SubTitle>{t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.SUBSECTIONS.INCOME_HEALTH_INSURANCE')}</SubTitle>

            <FormCurrencyInput
                id={`${basePath}.nettoeinkommenMonatlich` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.INCOME.MAIN_INCOME')}
                control={control}
                min={0}
            />

            <FormCurrencyInput
                id={`${basePath}.nettoeinkommenVorletzterMonat` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.INCOME.SALARY_LAST_MONTH')}
                control={control}
                min={0}
            />

            <FormCurrencyInput
                id={`${basePath}.nettoeinkommenVorVorletzterMonat` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.INCOME.SALARY_BEFORE_LAST_MONTH')}
                control={control}
                min={0}
            />

            <FormCurrencyInput
                id={`${basePath}.kumulierterJahreswert` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.INCOME.CUMULATIVE_ANNUAL_VALUE')}
                control={control}
                min={0}
            />

            <SubTitle>{t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.SUBSECTIONS.LIMITATIONS')}</SubTitle>

            {/* Probezeit - RadioButtons */}
            <div className="flex gap-2.5 items-center">
                <span className="w-[175px] shrink-0 text-sm text-midnight text-left">
                    {t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.PROBATIONARY_PERIOD.TITLE')}
                </span>
                <Controller
                    name={`${basePath}.inProbezeit` as Path<ApplicantCreation>}
                    control={control}
                    render={({ field }) => (
                        <div className="flex gap-4 w-full justify-between">
                            <RadioButton
                                showBorder={true}
                                wrapperClassName="flex w-full justify-center"
                                id={`${basePath}.inProbezeit.yes`}
                                name={`${basePath}.inProbezeit`}
                                value="true"
                                checked={field.value === true}
                                onChange={() => field.onChange(true)}
                                label={t('VALUES.YES')}
                            />
                            <RadioButton
                                showBorder={true}
                                wrapperClassName="flex w-full justify-center"
                                id={`${basePath}.inProbezeit.no`}
                                name={`${basePath}.inProbezeit`}
                                value="false"
                                checked={field.value === false}
                                onChange={() => field.onChange(false)}
                                label={t('VALUES.NO')}
                            />
                            <RadioButton
                                showBorder={true}
                                wrapperClassName="flex w-full justify-center"
                                id={`${basePath}.inProbezeit.null`}
                                name={`${basePath}.inProbezeit`}
                                value="null"
                                checked={field.value === null || field.value === undefined}
                                onChange={() => field.onChange(null)}
                                label={t('VALUES.NOT_SPECIFIED')}
                            />
                        </div>
                    )}
                />
            </div>

            {inProbezeit === true && (
                <FormDateInput
                    id={`${basePath}.inProbezeitBis` as Path<ApplicantCreation>}
                    label={t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.PROBATIONARY_PERIOD.PROBATIONARY_PERIOD_END')}
                    control={control}
                />
            )}

            <SubTitle>{t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.SUBSECTIONS.EMPLOYER')}</SubTitle>

            <TextInput
                id={`${basePath}.arbeitgeber.name`}
                label={t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.EMPLOYER_NAME')}
                labelClassName="w-[175px] justify-start"
                {...register(`${basePath}.arbeitgeber.name` as Path<ApplicantCreation>)}
            />

            {/* Straße, Hausnummer */}
            <div className="flex gap-2.5 w-full justify-between items-start">
                <label className="min-w-[175px] justify-start text-sm text-midnight text-left mt-1.5">
                    {t('ADDRESS.STREET')}, {t('ADDRESS.HOUSE_NUMBER')}
                </label>
                <div className="flex flex-col w-3/4">
                    <TextInput
                        id={`${basePath}.arbeitgeber.anschrift.strasse`}
                        {...register(`${basePath}.arbeitgeber.anschrift.strasse` as Path<ApplicantCreation>)}
                    />
                </div>
                <div className="flex flex-col w-1/4">
                    <TextInput
                        id={`${basePath}.arbeitgeber.anschrift.hausnummer`}
                        {...register(`${basePath}.arbeitgeber.anschrift.hausnummer` as Path<ApplicantCreation>)}
                    />
                </div>
            </div>

            {/* PLZ, Ort */}
            <div className="flex gap-2.5 w-full justify-between items-start">
                <label className="min-w-[175px] justify-start text-sm text-midnight text-left mt-1.5">
                    {t('ADDRESS.ZIP_CODE_SHORT')}, {t('ADDRESS.LOCATION')}
                </label>
                <div className="flex flex-col w-1/4">
                    <TextInput
                        id={`${basePath}.arbeitgeber.anschrift.plz`}
                        {...register(`${basePath}.arbeitgeber.anschrift.plz` as Path<ApplicantCreation>)}
                    />
                </div>
                <div className="flex flex-col w-3/4">
                    <TextInput
                        id={`${basePath}.arbeitgeber.anschrift.ort`}
                        {...register(`${basePath}.arbeitgeber.anschrift.ort` as Path<ApplicantCreation>)}
                    />
                </div>
            </div>

            <FormDropdown
                id={`${basePath}.arbeitgeber.anschrift.land` as Path<ApplicantCreation>}
                label={t('ADDRESS.COUNTRY')}
                options={countryOptions}
                control={control}
            />
        </div>
    );
};
